import { PERSONALIZATION_QUERY_KEYS } from '@/data/Content/RepurchaseCoupon';
import { DPP_JOURNEY_QUERY_KEY } from '@/data/dpp/constants';
import type { DppMemberPromotion } from '@/data/dpp/types';
import type { UrlObject } from 'url';

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
const RELATIVE_ORIGIN = 'https://relative.invalid';

/**
 * Tiempo restante en texto corto ("3 días", "1 día", "5 h"; con withHours, "3 días 14 h");
 * undefined si ya venció o no es fecha.
 */
export const formatDppTimeLeft = (until: string, now = Date.now(), withHours = false) => {
	const left = new Date(until).getTime() - now;
	if (!(left > 0)) {
		return undefined;
	}
	const hours = Math.floor((left % ONE_DAY_MS) / ONE_HOUR_MS);
	if (left >= ONE_DAY_MS) {
		const days = Math.floor(left / ONE_DAY_MS);
		const label = days === 1 ? '1 día' : `${days} días`;
		return withHours && hours > 0 ? `${label} ${hours} h` : label;
	}
	return `${Math.max(1, hours)} h`;
};

/** Tiempo restante de un cupón que ya se puede usar; undefined si aún no aplica o venció. */
export const getDppRedeemableTimeLeft = (
	promotion: DppMemberPromotion,
	now = Date.now(),
	withHours = false
) =>
	promotion.reason === 'ELIGIBLE' && promotion.eligibleUntil
		? formatDppTimeLeft(promotion.eligibleUntil, now, withHours)
		: undefined;

export const getDppFirstName = (firstName?: string) =>
	firstName?.trim().split(/\s+/)[0] || undefined;

/** Agrega a la landing los parámetros que la personalizan con la promoción del cliente. */
export const buildDppLandingHref = (
	href: string,
	promotion: DppMemberPromotion,
	firstName?: string
): UrlObject | string => {
	const url = new URL(href, RELATIVE_ORIGIN);
	const params: Record<string, string | undefined> = {
		[PERSONALIZATION_QUERY_KEYS.promotionId]: promotion.promotionName,
		[PERSONALIZATION_QUERY_KEYS.percentage]: String(promotion.discountPercent),
		[PERSONALIZATION_QUERY_KEYS.partNumbers]: promotion.offeredPartNumbers.join(','),
		[PERSONALIZATION_QUERY_KEYS.firstName]: firstName,
		[PERSONALIZATION_QUERY_KEYS.expiration]: promotion.eligibleUntil ?? undefined,
		[DPP_JOURNEY_QUERY_KEY]: promotion.journeyId,
	};
	Object.entries(params).forEach(([key, value]) => {
		if (value) {
			url.searchParams.set(key, value);
		}
	});
	if (url.origin !== RELATIVE_ORIGIN) {
		return url.toString();
	}
	return {
		pathname: url.pathname,
		query: Object.fromEntries(url.searchParams),
		hash: url.hash || undefined,
	};
};
